import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import Logo from "../../assets/images/Logo";
import pipe from "../../assets/images/pipe.svg";
import "./Layout.scss";
import { useEffect, useRef, useState } from "react";
import { Menu } from "primereact/menu";
import { Avatar } from "primereact/avatar";
import Footer from "../Footer/Footer";
import CustomButton from "../Common/CustomButton/CustomButton";
import { useConfigStore } from "../../stores/config";
import { useAuthStore } from "../../stores/authStore";

interface BreadcrumbItem {
  label: string;
  url?: string;
}

interface RouteMeta {
  breadcrumbs: BreadcrumbItem[];
  header: {
    title: string;
    subtitle: string;
  };
}

const routeConfig: Record<string, RouteMeta> = {
  "/": {
    breadcrumbs: [{ label: "Summary Dashboard" }],
    header: { title: "Summary Dashboard", subtitle: "" },
  },
  "/configuration": {
    breadcrumbs: [{ label: "Configuration Portal" }],
    header: {
      title: "Configuration Portal",
      subtitle: "Manage and Configure Package Bundle",
    },
  },
  "/assessment": {
    breadcrumbs: [{ label: "Negotiation Workspace" }],
    header: {
      title: "Negotiation Workspace",
      subtitle:
        "Unlocking strategic advantage through responsible and scalable AI integration.",
    },
  },
};

type QueueStatus = "PENDING" | "SUCCESS" | "FAILED" | "PARTIAL_FAILED";

interface QueueEntry {
  negotiation_id: string;
  status: QueueStatus;
  task_id?: string;
  updatedAt?: string;
}


// 👇 Consistent key for an ARRAY of queue entries (per negotiation)
const STORAGE_KEY = "queuingDataList";
const INTERVAL_MS = 8000;

// ---------- localStorage helpers (safe + array enforced) ----------
const readQueueList = (): QueueEntry[] => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as QueueEntry[];
    if (parsed && typeof parsed === "object") return [parsed as QueueEntry];
    return [];
  } catch {
    return [];
  }
};

const writeQueueList = (list: QueueEntry[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  // fire same-tab notification (storage event won't fire in the same document)
  window.dispatchEvent(new CustomEvent("queue:updated"));
};

const upsertQueueEntry = (entry: QueueEntry) => {
  const list = readQueueList();
  const idx = list.findIndex((e) => e.negotiation_id === entry.negotiation_id);
  const updated: QueueEntry = { ...entry, updatedAt: new Date().toISOString() };
  if (idx >= 0) list[idx] = { ...list[idx], ...updated };
  else list.push(updated);
  writeQueueList(list);
};

const updateEntryStatus = (negotiation_id: string, patch: Partial<Omit<QueueEntry, "negotiation_id">>) => {
  const list = readQueueList();
  const idx = list.findIndex((e) => e.negotiation_id === negotiation_id);
  if (idx === -1) return;
  list[idx] = { ...list[idx], ...patch, updatedAt: new Date().toISOString() };
  writeQueueList(list);
};

const getEntry = (negotiation_id: string): QueueEntry | undefined =>
  readQueueList().find((e) => e.negotiation_id === negotiation_id);

// -----------------------------------------------------------------

const Layout: React.FC = () => {
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);
  const [header, setHeader] = useState<RouteMeta["header"] | null>(null);
  const [btnProperty, setBtnProperty] = useState({
    name: "Configuration Portal",
    visible: true,
    link: "",
  });
  const navigate = useNavigate();
  const breadcrumbHome = { icon: "pi pi-home", command: () => navigate("/") };
  const location = useLocation();
  const token = useAuthStore((state) => state.token);
  const [regenerateBtn, setRegenerateBtn] = useState("Regenerate Target & Tactics");
  const getStatusOfTargetnTactics =
    useConfigStore.getState().getStatusOfTargetnTactics;

  // workspace became negotiation_id in BE
  const workspace = JSON.parse(
    sessionStorage.getItem("negotiation_workspace") || "{}"
  );
  useEffect(() => {
    if (import.meta.env.DEV && !token) {
      navigate("/login");
      return;
    }
  }, [token, navigate]);


  useEffect(() => {
    const path = location.pathname;

    const staticMeta = routeConfig[path];
    if (staticMeta) {
      const shouldDecorate = path !== "/";

      const decoratedHeader =
        shouldDecorate && workspace?.name
          ? {
              title: `${staticMeta.header.title} – ${workspace.name}`,
              subtitle: workspace?.description || staticMeta.header.subtitle,
            }
          : staticMeta.header;

      const decoratedCrumbs =
        shouldDecorate && workspace?.name
          ? [...staticMeta.breadcrumbs, { label: workspace.name }]
          : staticMeta.breadcrumbs;

      if (path === "/") {
        sessionStorage.removeItem("negotiation_workspace");
      }

      setBreadcrumbItems(decoratedCrumbs);
      setHeader(decoratedHeader);
      return;
    }

    // /assessment
    if (path.includes("/assessment")) {
      setBreadcrumbItems([
        { label: "Negotiation" },
        { label: workspace?.name || "..." },
      ]);
      setBtnProperty({
        name: "Configuration Portal",
        visible: true,
        link: `/negotiation-packages/${workspace?.negotiation_id}/configure`,
      });
      setHeader({
        title: `Negotiation - ${workspace?.name || ""}`,
        subtitle: workspace?.description || "",
      });
      return;
    }

    // /configuration
    if (path.includes("/configure")) {
      setBreadcrumbItems([
        { label: "Configuration Portal" },
        { label: workspace?.name || "..." },
      ]);
      setBtnProperty({
        name: "Negotiation",
        visible: true,
        link: `/negotiation-packages/${workspace?.negotiation_id}/assessment`,
      });
      setHeader({
        title: `Configuration - ${workspace?.name || ""}`,
        subtitle: workspace?.description || "",
      });
      return;
    }
    setBreadcrumbItems([]);
    setHeader({
      title: workspace?.name
        ? `Workspace - ${workspace.name}`
        : "Configuration Portal",
      subtitle: workspace?.description || "Manage and Configure Packages",
    });
  }, [location.pathname, workspace?.name, workspace?.description]);

  const { negotiation_id } = useParams<{ negotiation_id?: string }>();

  // ---------- Long polling per negotiation_id (stops on SUCCESS) ----------
  useEffect(() => {
    if (!negotiation_id) return;

    let stopped = false;
    let timer: number | null = null;

    const clearTimer = () => {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = (ms: number = INTERVAL_MS) => {
      clearTimer();
      timer = window.setTimeout(tick, ms);
    };

    const tick = async () => {
      if (stopped) return;

      const entry = getEntry(negotiation_id);

      if (entry?.status === "PENDING" && entry.task_id) {
        setRegenerateBtn("Regenerating...");
        try {
          const response = (await getStatusOfTargetnTactics(
            entry.task_id
          )) as any;

          // ✅ stop polling when backend says SUCCESS
          if (response?.status === "SUCCESS") {
            setRegenerateBtn("Regenerated Target & Tactics");
            updateEntryStatus(negotiation_id, { status: "SUCCESS" });
            stopped = true;
            clearTimer();
            return;
          }
        } catch {
          // keep polling despite transient errors
        }
        schedule(INTERVAL_MS);
        return;
      }
	  else if (entry?.status === "SUCCESS") {
		setRegenerateBtn("Regenerated Target & Tactics");
	  }
	  else setRegenerateBtn("Regenerate Target & Tactics");
      schedule(Math.max(INTERVAL_MS, 8000));
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      clearTimer();
      void tick();
    };

    const onLocalQueueUpdated = () => {
      clearTimer();
      void tick();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("queue:updated", onLocalQueueUpdated as EventListener);

    void tick(); // initial kick

    return () => {
      stopped = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("queue:updated", onLocalQueueUpdated as EventListener);
      clearTimer();
    };
  }, [negotiation_id, getStatusOfTargetnTactics, setRegenerateBtn]);
  // -----------------------------------------------------------------------

  const menuLeft = useRef<Menu | null>(null);
  const items = [
    {
      label: "Logout",
      icon: "pi pi-sign-out",
      command: () => {
        localStorage.clear();
        useAuthStore.getState().setToken("");
        window.location.assign("/login");
      },
    },
  ];

  const handleRegenerate = async () => {
    const payload = {
      message: "Start Targets & Strategies",
      negotiation_id: workspace?.negotiation_id, // only this is required in future
      agent_type: "negotiation_strategy_v3",
    };

    const { task_id, status }: any =
      await useConfigStore.getState().regerateTargetnTactics(payload);

    // ✅ UPSERT (do not overwrite the whole list)
    upsertQueueEntry({
      negotiation_id: String(negotiation_id ?? workspace?.negotiation_id),
      status,
      task_id,
    });

    // optional: immediate first check
    if (status === "PENDING") {
      await useConfigStore.getState().getStatusOfTargetnTactics(task_id);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <>
        <div className="topbar">
          <div className="logo flex justify-between ">
            <div className="brand-lockup">
              <Logo />
              <img src={pipe} alt="" className="brand-separator" />
              <span className="brand-product">NegotiationIQ</span>
            </div>
            <Avatar
              icon="pi pi-user"
              shape="circle"
              onClick={(event) => (menuLeft.current as any)?.toggle(event)}
            />
            <Menu model={items} popup ref={menuLeft} id="popup_menu_left" />
          </div>
        </div>

        <div className="wrapper">
          <BreadCrumb
            className="custom-breadcrumb"
            model={breadcrumbItems}
            home={breadcrumbHome}
          />
          <div>
            <p className="header-title">{header?.title}</p>
            <p className="header-p">{header?.subtitle}</p>
          </div>
          {workspace &&
            workspace?.negotiation_id &&
            workspace.status !== "New" && (
              <div
                className="flex items-center gap-2  justify-between"
                style={{ width: "100%" }}
              >
                <CustomButton
                  label={btnProperty.name}
                  variant="tertiary"
                  onClick={() => navigate(btnProperty.link)}
                  iconsOnly={false}
                  icon="pi pi-arrow-left"
                />

                {btnProperty.name === "Negotiation" && (
                  <CustomButton
                    label={regenerateBtn}
                    variant="primary"
                    onClick={handleRegenerate}
                    iconsOnly={false}
                    icon="pi pi-replay"
                    disabled={regenerateBtn === "Regenerating..." || regenerateBtn === "Regenerated Target & Tactics"}
                  />
                )}
              </div>
            )}
        </div>
      </>
      <main style={{ paddingBottom: "35px" }}>
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
